import { cn } from '../../lib/cn';
import { BearyGuide, BearySprite } from './BearySprite';

const toneClasses = {
  neutral: 'border-slate-200 bg-slate-50 text-slate-700',
  loading: 'border-amber-200 bg-amber-50 text-amber-800',
  success: 'border-emerald-200 bg-emerald-50 text-emerald-800',
  error: 'border-red-200 bg-red-50 text-red-800',
  warning: 'border-orange-200 bg-orange-50 text-orange-800',
  accent: 'border-blue-200 bg-blue-50 text-blue-700',
  family: 'border-emerald-200 bg-emerald-50 text-emerald-700',
};

export const inputClassName =
  'w-full rounded-xl border border-app-border/80 bg-app-surface px-4 py-3 text-sm text-app-ink outline-none transition placeholder:text-app-muted/70 focus:border-app-accent/40 focus:ring-2 focus:ring-app-accent/20';

export function SectionCard({ children, className }) {
  return (
    <section
      className={cn(
        'rounded-[22px] border border-app-border/70 bg-app-surface/95 p-6 shadow-subtle lg:p-7',
        className
      )}
    >
      {children}
    </section>
  );
}

export function SectionTitle({ title, copy, action, className }) {
  return (
    <div className={cn('flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between', className)}>
      <div>
        <h2 className="m-0 text-lg font-semibold text-app-ink">{title}</h2>
        {copy ? <p className="mt-1 text-sm leading-6 text-app-muted">{copy}</p> : null}
      </div>
      {action}
    </div>
  );
}

export function StatusPill({ children, tone = 'neutral', className }) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold capitalize',
        toneClasses[tone] || toneClasses.neutral,
        className
      )}
    >
      {tone === 'loading' ? (
        <BearySprite preset="loading" size={28} label="Beary is helping load this" />
      ) : null}
      {children}
    </span>
  );
}

export function MetricCard({ label, value, note, accentClassName, className, icon: Icon }) {
  return (
    <div className={cn('rounded-2xl border border-app-border/70 bg-app-surface/85 p-5 shadow-subtle', className)}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="m-0 text-xs font-semibold uppercase tracking-[0.14em] text-app-muted">{label}</p>
          <p className={cn('mt-2 text-3xl font-semibold tracking-tight text-app-ink', accentClassName)}>{value}</p>
        </div>
        {Icon ? (
          <span className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-app-surface-alt text-app-muted">
            <Icon className="h-5 w-5" aria-hidden="true" />
          </span>
        ) : null}
      </div>
      {note ? <p className="mt-3 text-sm leading-7 text-app-muted">{note}</p> : null}
    </div>
  );
}

export function EmptyState({ title, copy, action, className, beary = true }) {
  return (
    <div
      className={cn(
        'rounded-[22px] border border-dashed border-app-border bg-app-surface/70 px-6 py-12 text-center shadow-subtle lg:py-14',
        className
      )}
    >
      {beary ? (
        <div className="mb-4 flex justify-center">
          <BearySprite preset="wave" size={92} label="Beary family guide" />
        </div>
      ) : null}
      <p className="m-0 text-lg font-semibold text-app-ink">{title}</p>
      <p className="mt-3 text-sm leading-7 text-app-muted">{copy}</p>
      {action ? <div className="mt-6">{action}</div> : null}
    </div>
  );
}

export function PageHero({ eyebrow, title, lede, action, className, bearyMessage, bearyPreset = 'guide' }) {
  return (
    <SectionCard className={cn('bg-app-shell overflow-hidden', className)}>
      <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
        <div className="max-w-3xl">
          <p className="m-0 text-xs font-semibold uppercase tracking-[0.18em] text-app-accent-strong">
            {eyebrow}
          </p>
          <h1 className="mt-4 text-4xl font-semibold tracking-tight text-app-ink">{title}</h1>
          {lede ? <p className="mt-4 text-base leading-8 text-app-muted">{lede}</p> : null}
          {bearyMessage ? (
            <BearyGuide
              className="mt-5 max-w-xl"
              preset={bearyPreset}
              size={80}
              message={bearyMessage}
            />
          ) : null}
        </div>
        {action ? <div className="shrink-0">{action}</div> : null}
      </div>
    </SectionCard>
  );
}
